import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { X, Plus, Lock, User as UserIcon, KeyRound, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { canEditExistingDates } from "@/lib/permissions";
import PhoneInput from "@/components/PhoneInput";
import PasswordInput from "@/components/PasswordInput";
import SMSConsentBlock from "@/components/SMSConsentBlock";

export default function Settings() {
  const { user: me, refresh } = useAuth();
  const isSuper = canEditExistingDates(me);
  const [settings, setSettings] = useState(null);
  const [newPhase, setNewPhase] = useState("");
  const [newCriterion, setNewCriterion] = useState("");
  const [newTimeframe, setNewTimeframe] = useState("");
  const [busy, setBusy] = useState(false);

  // ---------- Account preferences (self profile) ----------
  const [profile, setProfile] = useState({ name: "", email: "", phone: "" });
  const [profileDirty, setProfileDirty] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [notif, setNotif] = useState({
    method: "email",
    events: { new_ideas: false, open_close: false, all_updates: true },
    auto_follow_new_ideas: true,
  });
  const [notifSaving, setNotifSaving] = useState(false);
  // SMS consent: has the user already agreed to receive automated texts?
  // Persisted on the user record; when they switch method to sms/both and no
  // prior consent is on file, we show the consent block and hold the pending
  // method change until they check it.
  const [smsConsent, setSmsConsent] = useState(false);
  const [pendingSmsMethod, setPendingSmsMethod] = useState(null);

  useEffect(() => {
    if (me) {
      setProfile({ name: me.name || "", email: me.email || "", phone: me.phone || "" });
      const prefs = me.notification_preferences || {};
      setNotif({
        method: prefs.method || "email",
        events: {
          new_ideas: !!(prefs.events?.new_ideas),
          open_close: !!(prefs.events?.open_close),
          all_updates: prefs.events?.all_updates ?? !prefs.events,
        },
        auto_follow_new_ideas: prefs.auto_follow_new_ideas ?? !prefs.method, // default on when no prefs yet
      });
      setSmsConsent(!!prefs.sms_consent);
      setPendingSmsMethod(null);
    }
  }, [me?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const patchProfile = (patch) => {
    setProfile((p) => ({ ...p, ...patch }));
    setProfileDirty(true);
  };
  const saveProfile = async () => {
    if (profileSaving) return;
    setProfileSaving(true);
    try {
      await api.patch("/auth/me", {
        name: profile.name,
        email: profile.email,
        phone: profile.phone,
      });
      await refresh?.();
      setProfileDirty(false);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update profile");
    } finally {
      setProfileSaving(false);
    }
  };
  const changePassword = async () => {
    if (pwSaving) return;
    if (!pw.current || !pw.next) { toast.error("Enter both current and new password"); return; }
    if (pw.next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.next !== pw.confirm) { toast.error("New passwords don't match"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      setPw({ current: "", next: "", confirm: "" });
      toast.success("Password updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    } finally {
      setPwSaving(false);
    }
  };

  const saveNotif = async (next) => {
    if (notifSaving) return;
    setNotifSaving(true);
    try {
      await api.patch("/auth/me", { notification_preferences: next });
      await refresh?.();
      toast.success("Notifications updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save notifications");
    } finally {
      setNotifSaving(false);
    }
  };
  const patchNotifMethod = (method) => {
    // If the user is switching to a method that uses SMS and they have not yet
    // given consent for automated texts, hold the change locally and show the
    // consent block. The actual PATCH fires from `agreeSmsConsent` below.
    const needsSms = method === "sms" || method === "both";
    if (needsSms && !smsConsent) {
      setPendingSmsMethod(method);
      return;
    }
    setPendingSmsMethod(null);
    const next = { ...notif, method };
    setNotif(next);
    saveNotif(next);
  };
  const agreeSmsConsent = (checked) => {
    // Toggling the checkbox commits or reverses the SMS consent on the record.
    if (!checked) {
      // Uncheck cancels the pending switch — stay on the previously-saved method.
      setPendingSmsMethod(null);
      return;
    }
    if (!pendingSmsMethod) return;
    setSmsConsent(true);
    const next = { ...notif, method: pendingSmsMethod };
    setNotif(next);
    setPendingSmsMethod(null);
    saveNotif({ ...next, sms_consent: true });
  };
  const patchNotifEvent = (key) => {
    const next = { ...notif, events: { ...notif.events, [key]: !notif.events[key] } };
    setNotif(next);
    saveNotif(next);
  };
  const patchAutoFollow = (v) => {
    const next = { ...notif, auto_follow_new_ideas: v };
    setNotif(next);
    saveNotif(next);
  };

  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)); }, []);
  if (!settings) return <div className="p-8 text-zinc-500 text-sm">Loading…</div>;

  const update = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", patch);
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setBusy(false);
    }
  };

  const addPhase = () => {
    if (!newPhase.trim()) return;
    const next = [...(settings.default_phases || []), newPhase.trim()];
    setNewPhase("");
    update({ default_phases: next });
  };
  const removePhase = (p) => update({ default_phases: settings.default_phases.filter((x) => x !== p) });

  const addCriterion = () => {
    if (!newCriterion.trim()) return;
    const next = [...(settings.setup_criteria_templates || []), newCriterion.trim()];
    setNewCriterion("");
    update({ setup_criteria_templates: next });
  };
  const removeCriterion = (c) => update({ setup_criteria_templates: settings.setup_criteria_templates.filter((x) => x !== c) });

  const addTimeframe = () => {
    if (!newTimeframe.trim()) return;
    const next = [...(settings.timeframes || []), newTimeframe.trim()];
    setNewTimeframe("");
    update({ timeframes: next });
  };
  const removeTimeframe = (t) => update({ timeframes: (settings.timeframes || []).filter((x) => x !== t) });

  return (
    <div className="p-8 max-w-3xl">
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-semibold tracking-tight" data-testid="page-title">Settings</h1>
        <p className="text-sm text-zinc-500">Customize your trade phases and setup criteria templates.</p>
      </div>

      <div className="space-y-6">
        <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm p-5" data-testid="account-preferences">
          <h3 className="font-heading text-sm uppercase tracking-wider text-zinc-400 mb-4 inline-flex items-center gap-2">
            <UserIcon className="w-4 h-4 text-blue-400" /> Account Preferences
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Name</Label>
              <Input
                value={profile.name}
                onChange={(e) => patchProfile({ name: e.target.value })}
                data-testid="profile-name-input"
                className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Email</Label>
              <Input
                type="email"
                value={profile.email}
                onChange={(e) => patchProfile({ email: e.target.value })}
                data-testid="profile-email-input"
                className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Phone</Label>
              <div className="mt-1">
                <PhoneInput
                  value={profile.phone}
                  onChange={(v) => patchProfile({ phone: v })}
                  testIdPrefix="profile-phone"
                />
              </div>
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button
              onClick={saveProfile}
              disabled={!profileDirty || profileSaving}
              data-testid="profile-save-btn"
              className="rounded-sm bg-blue-600 hover:bg-blue-500 h-9 disabled:opacity-50"
            >
              {profileSaving ? "Saving…" : "Save changes"}
            </Button>
          </div>

          <div className="mt-6 pt-4 border-t border-zinc-900">
            <h4 className="font-heading text-xs uppercase tracking-wider text-zinc-500 mb-3 inline-flex items-center gap-2">
              <KeyRound className="w-3.5 h-3.5" /> Change Password
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Current</Label>
                <PasswordInput value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current-input" className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-zinc-500">New</Label>
                <PasswordInput value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="pw-new-input" className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-wider text-zinc-500">Confirm</Label>
                <PasswordInput value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm-input" className="mt-1 bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
              </div>
            </div>
            {(() => {
              const tooShort = pw.next.length > 0 && pw.next.length < 8;
              const mismatch = pw.next.length > 0 && pw.confirm.length > 0 && pw.next !== pw.confirm;
              const match = pw.next.length >= 8 && pw.confirm.length > 0 && pw.next === pw.confirm;
              if (tooShort) return <p className="text-[11px] text-amber-400 font-mono mt-2" data-testid="pw-hint">Minimum 8 characters.</p>;
              if (mismatch) return <p className="text-[11px] text-rose-400 font-mono mt-2" data-testid="pw-hint">Passwords do not match.</p>;
              if (match) return <p className="text-[11px] text-emerald-400 font-mono mt-2" data-testid="pw-hint">Passwords match.</p>;
              return <p className="text-[11px] text-zinc-600 mt-2" data-testid="pw-hint">Minimum 8 characters.</p>;
            })()}
            <div className="mt-3 flex justify-end">
              <Button
                onClick={changePassword}
                disabled={pwSaving || !pw.current || !pw.next || !pw.confirm || pw.next !== pw.confirm || pw.next.length < 8}
                data-testid="pw-save-btn"
                className="rounded-sm bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 text-zinc-200 h-9 disabled:opacity-50"
              >
                {pwSaving ? "Updating…" : "Update password"}
              </Button>
            </div>
          </div>
        </div>

        <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm p-5" data-testid="notifications-card">
          <h3 className="font-heading text-sm uppercase tracking-wider text-zinc-400 mb-4 inline-flex items-center gap-2">
            <Bell className="w-4 h-4 text-blue-400" /> Trade Notifications
          </h3>
          <p className="text-xs text-zinc-500 mb-4">Get alerts when trades you follow are updated. You can follow a trade from its Trade Details page.</p>

          <div className="mb-5">
            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Notification Method</Label>
            <div className="flex flex-wrap gap-2" data-testid="notif-method-group">
              {[
                { v: "email", label: "Email" },
                { v: "sms", label: "Text" },
                { v: "both", label: "Email + Text" },
                { v: "none", label: "Off" },
              ].map((opt) => {
                const isActive = pendingSmsMethod ? pendingSmsMethod === opt.v : notif.method === opt.v;
                return (
                <button
                  key={opt.v}
                  onClick={() => patchNotifMethod(opt.v)}
                  disabled={notifSaving}
                  data-testid={`notif-method-${opt.v}`}
                  className={`px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-wider border transition-colors ${
                    isActive
                      ? "border-blue-500/50 bg-blue-500/10 text-blue-400"
                      : "border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
                  } disabled:opacity-50`}
                >
                  {opt.label}
                </button>
              );})}
            </div>
            {pendingSmsMethod && (
              <SMSConsentBlock checked={false} onCheckedChange={agreeSmsConsent} testIdPrefix="settings-sms-consent" />
            )}
            {notif.method !== "email" && notif.method !== "none" && !me?.phone && (
              <p className="text-[11px] text-amber-400 font-mono mt-2">Add a phone number above to receive text alerts.</p>
            )}
          </div>

          <label
            className="flex items-start gap-3 p-2 mb-5 rounded-sm border border-zinc-900 hover:border-zinc-800 cursor-pointer"
            data-testid="notif-auto-follow"
          >
            <Switch
              checked={!!notif.auto_follow_new_ideas}
              onCheckedChange={(v) => patchAutoFollow(v)}
              disabled={notifSaving}
              data-testid="notif-auto-follow-switch"
            />
            <div>
              <div className="text-sm text-zinc-200">Automatically follow all New Trade Ideas</div>
              <div className="text-[11px] text-zinc-500">When on, any new trade added to the shared journal is auto-followed for you.</div>
            </div>
          </label>

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500 mb-2 block">Notify me about</Label>
            <div className="space-y-2" data-testid="notif-events-group">
              {[
                { key: "new_ideas", label: "New Trade Ideas", hint: "A new trade is added to the shared journal." },
                { key: "open_close", label: "Trade Open / Close", hint: "A followed trade is Opened, Added to, Trimmed, or Closed" },
                { key: "all_updates", label: "All Trade Updates", hint: "Every phase update on a followed trade." },
              ].map((opt) => {
                const isAll = opt.key === "all_updates";
                const disabledByAll = !isAll && !!notif.events.all_updates;
                return (
                  <label
                    key={opt.key}
                    data-testid={`notif-event-${opt.key}`}
                    className={`flex items-start gap-3 p-2 rounded-sm border border-zinc-900 ${disabledByAll ? "opacity-50 cursor-not-allowed" : "hover:border-zinc-800 cursor-pointer"}`}
                  >
                    <Switch
                      checked={isAll ? !!notif.events.all_updates : (!!notif.events.all_updates || !!notif.events[opt.key])}
                      onCheckedChange={() => patchNotifEvent(opt.key)}
                      disabled={notifSaving || disabledByAll}
                      data-testid={`notif-event-${opt.key}-switch`}
                    />
                    <div>
                      <div className="text-sm text-zinc-200">{opt.label}</div>
                      <div className="text-[11px] text-zinc-500">{opt.hint}</div>
                      {disabledByAll && (
                        <div className="text-[10px] text-blue-400 font-mono mt-0.5 uppercase tracking-wider">Included in All Trade Updates</div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
          <p className="text-[10px] text-zinc-600 font-mono mt-4">Delivery is queued; text alerts arrive within a few minutes.</p>
        </div>

        <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm p-5">
          <h3 className="font-heading text-sm uppercase tracking-wider text-zinc-400 mb-3">Account Size (default)</h3>
          <div className="flex items-center gap-3">
            <span className="text-zinc-500 font-mono">$</span>
            <Input type="number" value={settings.default_account_size || ""} onChange={(e) => setSettings({ ...settings, default_account_size: parseFloat(e.target.value) || 0 })} onBlur={() => update({ default_account_size: settings.default_account_size })} data-testid="account-size-input" className="max-w-xs bg-zinc-950 border-zinc-800 rounded-sm font-mono" />
          </div>
        </div>

        <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm p-5">
          <h3 className="font-heading text-sm uppercase tracking-wider text-zinc-400 mb-3">Trade Size % of Account</h3>
          <p className="text-xs text-zinc-500 mb-3">Your default position size as a percentage of account. Used in Trade Setup when a trade has no explicit size set.</p>
          <div className="flex items-center gap-3 mb-4">
            <Input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={settings.default_trade_size_pct ?? ""}
              onChange={(e) => setSettings({ ...settings, default_trade_size_pct: e.target.value === "" ? null : parseFloat(e.target.value) })}
              onBlur={() => update({ default_trade_size_pct: settings.default_trade_size_pct })}
              data-testid="trade-size-pct-input"
              className="max-w-xs bg-zinc-950 border-zinc-800 rounded-sm font-mono"
            />
            <span className="text-zinc-500 font-mono">%</span>
          </div>
          <div className="flex items-center gap-3 pt-3 border-t border-zinc-900">
            <Switch
              checked={settings.use_default_trade_size !== false}
              onCheckedChange={(v) => {
                setSettings({ ...settings, use_default_trade_size: v });
                update({ use_default_trade_size: v });
              }}
              data-testid="use-default-trade-size-toggle"
            />
            <Label className="text-sm text-zinc-300 cursor-pointer" onClick={() => {
              const v = !(settings.use_default_trade_size !== false);
              setSettings({ ...settings, use_default_trade_size: v });
              update({ use_default_trade_size: v });
            }}>
              Use this default in metrics calculations
            </Label>
          </div>
          <p className="text-[11px] text-zinc-600 mt-2">When on, Trade Setup will use your default trade size % to estimate share count and P&L for trades that don't specify a size.</p>
        </div>

        {isSuper && (<>
        <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm p-5">
          <h3 className="font-heading text-sm uppercase tracking-wider text-zinc-400 mb-3">Trade Phases</h3>
          <p className="text-xs text-zinc-500 mb-3">Global phases used in every trade timeline. <span className="text-amber-400">Base phases (locked)</span> are set by the Super Admin. You can also add custom phases per-trade.</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {settings.default_phases?.map((p) => {
              const isProtected = (settings.protected_phases || []).includes(p);
              const locked = isProtected && !isSuper;
              return (
                <span key={p} className={`inline-flex items-center gap-2 px-2 py-1 border rounded-sm text-sm font-mono ${isProtected ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800"}`}>
                  {locked && <Lock className="w-3 h-3 text-amber-400" />}
                  {p}
                  {!locked && (
                    <button onClick={() => removePhase(p)} data-testid={`remove-phase-${p}`} className="text-zinc-500 hover:text-rose-500"><X className="w-3 h-3" /></button>
                  )}
                </span>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Input value={newPhase} onChange={(e) => setNewPhase(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addPhase()} placeholder="Add phase (e.g. Scaled In)" data-testid="new-phase-input" className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xs" />
            <Button onClick={addPhase} data-testid="add-phase-btn" className="rounded-sm bg-blue-600 hover:bg-blue-500"><Plus className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm p-5">
          <h3 className="font-heading text-sm uppercase tracking-wider text-zinc-400 mb-3">Setup Criteria Templates</h3>
          <p className="text-xs text-zinc-500 mb-3">Reusable criteria that appear as checkboxes on trade entry. <span className="text-amber-400">Base criteria (locked)</span> are set by the Super Admin and can't be removed.</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {settings.setup_criteria_templates?.map((c) => {
              const isProtected = (settings.protected_criteria || []).includes(c);
              const locked = isProtected && !isSuper;
              return (
                <span key={c} className={`inline-flex items-center gap-2 px-2 py-1 border rounded-sm text-sm font-mono ${isProtected ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800"}`}>
                  {locked && <Lock className="w-3 h-3 text-amber-400" />}
                  {c}
                  {!locked && (
                    <button onClick={() => removeCriterion(c)} data-testid={`remove-crit-${c}`} className="text-zinc-500 hover:text-rose-500"><X className="w-3 h-3" /></button>
                  )}
                </span>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Input value={newCriterion} onChange={(e) => setNewCriterion(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCriterion()} placeholder="Add criterion (e.g. Volume Confirmation)" data-testid="new-crit-input" className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xs" />
            <Button onClick={addCriterion} data-testid="add-crit-btn" className="rounded-sm bg-blue-600 hover:bg-blue-500"><Plus className="w-4 h-4" /></Button>
          </div>
        </div>

        <div className="border border-zinc-900 bg-[#0a0a0a] rounded-sm p-5">
          <h3 className="font-heading text-sm uppercase tracking-wider text-zinc-400 mb-3">Timeframes</h3>
          <p className="text-xs text-zinc-500 mb-3">Timeframe options available in the New Trade and Phase dialogs. <span className="text-amber-400">Base timeframes (locked)</span> are set by the Super Admin.</p>
          <div className="flex flex-wrap gap-2 mb-3">
            {settings.timeframes?.map((t) => {
              const isProtected = (settings.protected_timeframes || []).includes(t);
              const locked = isProtected && !isSuper;
              return (
                <span key={t} className={`inline-flex items-center gap-2 px-2 py-1 border rounded-sm text-sm font-mono ${isProtected ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800"}`}>
                  {locked && <Lock className="w-3 h-3 text-amber-400" />}
                  {t}
                  {!locked && (
                    <button onClick={() => removeTimeframe(t)} data-testid={`remove-tf-${t}`} className="text-zinc-500 hover:text-rose-500"><X className="w-3 h-3" /></button>
                  )}
                </span>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Input value={newTimeframe} onChange={(e) => setNewTimeframe(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTimeframe()} placeholder="Add timeframe (e.g. 15m)" data-testid="new-tf-input" className="bg-zinc-950 border-zinc-800 rounded-sm max-w-xs" />
            <Button onClick={addTimeframe} data-testid="add-tf-btn" className="rounded-sm bg-blue-600 hover:bg-blue-500"><Plus className="w-4 h-4" /></Button>
          </div>
        </div>
        </>)}
      </div>
    </div>
  );
}
